import React from 'react';
import styled from '@emotion/native';
import { BaseText } from '@/shared/components/StyledText';
import { green, lightGreen, subheadline } from '@/shared/styles/colors';
import { Address } from '@/shared/types/generated';
import { formatAddress } from '@/shared/utils/sessionUtils';

interface InPersonAddressDisplayProps {
  address: Address | null | undefined;
  isPending: boolean;
  isStylist: boolean;
}

const InPersonAddressDisplay = ({
  address,
  isPending,
  isStylist,
}: InPersonAddressDisplayProps) => {
  if (!address || (!address.streetAddress && !address.city)) {
    return null;
  }

  const formattedAddress = formatAddress(address);
  const otherPartyLabel = isStylist ? 'client' : 'stylist';

  if (isPending) {
    return (
      <AddressSection>
        <AddressLabel>In-person session location:</AddressLabel>
        <PendingAddressText>{formattedAddress}</PendingAddressText>
        <ConfirmText>Use chat to confirm any final details.</ConfirmText>
      </AddressSection>
    );
  }

  return (
    <AddressSection>
      <ConfirmedAddressText>
        This session will take place in person at:{'\n'}
        <AddressLink>{formattedAddress}</AddressLink>
      </ConfirmedAddressText>
      <ConfirmText>
        Confirm any additional details via messages with your {otherPartyLabel}
      </ConfirmText>
    </AddressSection>
  );
};

const AddressSection = styled.View({
  alignItems: 'center',
  marginBottom: 16,
  paddingHorizontal: 20,
});

const AddressLabel = styled(BaseText)({
  fontSize: 14,
  color: lightGreen,
  textAlign: 'center',
  fontStyle: 'italic',
});

const PendingAddressText = styled(BaseText)({
  fontSize: 14,
  color: green,
  textAlign: 'center',
  textDecorationLine: 'underline',
  fontStyle: 'italic',
  marginBottom: 10,
});

const ConfirmedAddressText = styled(BaseText)({
  fontStyle: 'italic',
  fontSize: 14,
  color: subheadline,
  textAlign: 'center',
});

const AddressLink = styled(BaseText)({
  textDecorationLine: 'underline',
  fontSize: 14,
  fontWeight: 600,
  color: subheadline,
  textAlign: 'center',
});

const ConfirmText = styled(BaseText)({
  fontSize: 12,
  color: lightGreen,
  textAlign: 'center',
  fontStyle: 'italic',
  marginTop: 8,
  paddingHorizontal: 35,
});

// Keep backward-compatible export for existing usages
interface PendingRequestAddressProps {
  address: Address | null | undefined;
}

const PendingRequestAddress = ({ address }: PendingRequestAddressProps) => (
  <InPersonAddressDisplay address={address} isPending={true} isStylist={true} />
);

export default PendingRequestAddress;
export { InPersonAddressDisplay };
